import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, FileText, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { SOPEditorDialog } from "@/components/sop/SOPEditorDialog";
import { ListPageSkeleton } from "@/components/ui/skeleton-card";
import { EmptyState, ErrorState } from "@/components/ui/empty-state";
import { useToast } from "@/hooks/use-toast";

interface OutletContext {
  user: any;
  organizationId: string;
}

interface SOP {
  id: string;
  title: string;
  description: string;
  status: string;
  tags: string[];
  estimated_duration_minutes: number | null;
  last_published_at: string | null;
  created_at: string;
  _count?: { steps: number };
}

const SOPs = () => {
  const { user, organizationId } = useOutletContext<OutletContext>();
  const [sops, setSops] = useState<SOP[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const { toast } = useToast();

  const fetchSOPs = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from("sop_templates")
        .select("*")
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;

      setSops(data || []);
    } catch (err: any) {
      console.error("Error fetching SOPs:", err);
      setError(err.message || "Failed to load SOPs");
      toast({
        variant: "destructive",
        title: "Error loading SOPs",
        description: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSOPs();
  }, [organizationId]);

  const filteredSOPs = sops.filter((sop) =>
    sop.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sop.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sop.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "bg-accent/10 text-accent border-accent/20";
      case "DRAFT":
        return "bg-muted text-muted-foreground";
      case "ARCHIVED":
        return "bg-destructive/10 text-destructive";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  if (loading) {
    return <ListPageSkeleton count={3} />;
  }

  if (error) {
    return (
      <div className="container py-8 px-4">
        <ErrorState 
          title="Failed to load SOPs" 
          description={error}
          onRetry={fetchSOPs}
        />
      </div>
    );
  }

  return (
    <div className="container py-6 md:py-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold mb-1 md:mb-2">SOPs</h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Manage your standard operating procedures
          </p>
        </div>
        <Button onClick={() => setIsEditorOpen(true)} className="w-full sm:w-auto">
          <Plus className="mr-2 h-4 w-4" />
          Create SOP
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search SOPs..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {filteredSOPs.length === 0 ? (
        searchQuery ? (
          <EmptyState
            icon={FileText}
            title="No SOPs found"
            description="Try adjusting your search query"
          />
        ) : (
          <EmptyState
            icon={FileText}
            title="No SOPs yet"
            description="Get started by creating your first SOP or running the Ops Setup Copilot"
            action={{
              label: "Create SOP",
              onClick: () => setIsEditorOpen(true),
            }}
          />
        )
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredSOPs.map((sop) => (
            <Card key={sop.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between mb-2">
                  <Badge className={getStatusColor(sop.status)}>
                    {sop.status}
                  </Badge>
                  {sop.estimated_duration_minutes && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {sop.estimated_duration_minutes}min
                    </div>
                  )}
                </div>
                <CardTitle className="text-lg md:text-xl line-clamp-2">
                  {sop.title}
                </CardTitle>
                <CardDescription className="line-clamp-2">
                  {sop.description || "No description"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {sop.tags && sop.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {sop.tags.slice(0, 3).map((tag, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {sop.tags.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{sop.tags.length - 3}
                      </Badge>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {sop.last_published_at
                      ? `Published ${format(new Date(sop.last_published_at), "MMM d, yyyy")}`
                      : `Created ${format(new Date(sop.created_at), "MMM d, yyyy")}`}
                  </span>
                </div>
                <Link to={`/app/sops/${sop.id}`}>
                  <Button variant="outline" className="w-full" size="sm">
                    View Details
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <SOPEditorDialog
        open={isEditorOpen}
        onOpenChange={setIsEditorOpen}
        organizationId={organizationId}
        onSuccess={fetchSOPs}
      />
    </div>
  );
};

export default SOPs;
